import { hashCode } from './hash-code'
import { prune } from './prune'
import { randomIdentifierGenerator } from './random-identifier-generator'

/**
 * Common magic numbers live here.
 */
export enum Num {
    secToMs = 1000,
    decPrecision = 2
}

/**
 * Error keys
 * Used for script loading errors
 */
export enum Errors {
    MAGNITE_ERROR = 'magnite_error',
    GPT_ERROR = 'gpt_error'
}

/**
 * Helper functions.
 */
export const Utils = {
    /**
     * Repeatedly fires callback over duration with step value from 0-1.
     * Uses `requestAnimationFrame` unless forced to use timeout.
     */
    async anim(callback: (step: number) => any, durationMs?: number, force = false): Promise<void> {
        const startMs = Date.now()
        return new Promise((resolve) => {
            const next = () => force
                // tslint:disable-next-line: no-magic-numbers
                ? setTimeout(() => rec(), 83) // ~12 fps
                : requestAnimationFrame(() => rec())
            const rec = () => {
                const step = Math.min(1, (Date.now() - startMs) / (durationMs || Num.secToMs))
                const res = callback(step)
                if (step < 1 && res !== false) {
                    next()
                    return
                }
                resolve()
            }
            next()
        })
    },

    /**
     * De-camelCase a string.
     *
     * @param {string} str
     * @param {string} separator - optional. Space is used if not provided
     * @returns {string}
     */
    deCamelCase(str: string, separator = ' '): string {
        if (str) {
            return str
                .replace(/([a-z\d])([A-Z])/g, '$1' + separator + '$2')
                .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, '$1' + separator + '$2')
        }
        return undefined
    },

    /**
     * Creates new object with de-camelcased keys (non-recursive).
     *
     * @param {object} obj to convert keys
     * @returns {object}
     */
    deCamelCaseKeys(obj: any): object {
        if (obj) {
            const out: any = {}
            Object.keys(obj).forEach((key) => out[this.deCamelCase(key)] = obj[key])
            return out
        }
        return undefined
    },

    /**
     * Gets the current location object.
     */
    location(): Location {
        return window.location
    },

    /**
     * Gets the current page full url.
     */
    currentPageUrl(): string {
        return this.location().href
    },

    /**
     * Gets domain from url.
     */
    getDomain(): string {
        return this.location().hostname
    },

    /**
     * Gets path from url.
     */
    getPath(): string {
        return this.location().pathname
    },

    /**
     * Parse URL and return params as object
     * @param {string} url to parse
     * @returns {object}
     */
    urlParams(url: Location) {
        return (new URL(String(url))).searchParams
    },

    /**
     * Helper function to decode special characters
     *
     * @param {string}
     * @returns {string}
     */
    decodeHtmlEntity(str: string): string {
        const domParserHtmlEntities = new DOMParser()
        return domParserHtmlEntities.parseFromString(str, 'text/html').body.textContent
    },

    /**
     * CustomEvent factory.
     */
    event<E extends CustomEvent, T, D>(type: T, detail?: D): E {
        return new CustomEvent<D>(String(type), {bubbles: true, detail}) as E
    },

    hashCode,

    /**
     * Linearly interpolates between two numbers, given step from 0-1.
     */
    interpolateLin(start: number, end: number, step: number) {
        return start + (end - start) * step
    },

    /**
     * Check if an object is empty or not
     *
     * @param {object} obj
     * @returns {boolean}
     */
    isEmptyObject(obj: any): boolean {
        return !Object.entries(obj).length
    },

    /**
     * Creates new object with string properties (non-recursive).
     *
     * @param {object} obj
     * @returns {object} with string properties
     */
    objectValuesToStrings(obj: any): object {
        if (obj) {
            const out: any = {}
            Object.keys(obj).forEach((key) => out[key] = String(obj[key]))
            return out
        }
        return undefined
    },

    /**
     * Gets query parameter object or value of given param.
     */
    params(key?: string) {
        const params: { [_: string]: string } = {}
        location.search.slice(1).split('&')
            .map((kv) => kv.split('=').map((_) => _.trim()))
            .forEach(([k, v]) => params[k] = decodeURIComponent(v))
        if (key) {
            return params[key]
        }
        return params
    },

    /**
     * Races supplied promise with a timeout rejection.
     *
     * @param promise
     * @param millis
     */
    async promiseTimeout<T>(promise: Promise<T>, millis = Num.secToMs): Promise<T> {
        return Promise.race([
            promise,
            new Promise<T>((_, reject) =>
                setTimeout(() => reject(new Error(`Promise timed out after ${millis} ms.`)), millis))
        ])
    },

    prune,

    /**
     * Builds query string from params object.
     */
    query(params: { [_: string]: number | string | boolean }) {
        return Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')
    },

    /**
     * Retries a predicate until it returns true.
     *
     * @param predicate
     * @param times
     * @param sleep
     */
    async retry(predicate: () => Promise<boolean>, times = 3, sleep = 1000, timeout = 1000): Promise<void> {
        return new Promise(async (resolve, reject) => {
            for (let i = 0; i < times; i += 1) {
                const finished = await new Promise(async (resolveSub, rejectSub) => {
                    const res = await this.promiseTimeout(predicate(), timeout).catch(rejectSub)
                    if (!res && i < times - 1) {
                        setTimeout(resolveSub, sleep)
                    } else {
                        resolveSub(res)
                    }
                }).catch(console.log)

                if (finished) {
                    resolve()
                    break
                }
            }
            reject()
        })
    },

    randomIdentifierGenerator
}
